// src/components/Super Admin/Core Functions/EditSlotTemplateModal.tsx

import React, { useState, useEffect } from "react";
import "../../../css/SlotTemplateModal.css"; // Reuse the same CSS
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';


// Interface for the data coming from the parent (backend format)
interface BackendSlot {
  start_time: string;
  end_time: string;
  slot_type: string;
}

interface SlotTemplate {
  _id: string;
  name: string;
  slots?: BackendSlot[];
}

// Interface for the component's internal state (frontend format)
interface FrontendSlot {
  startTime: string;
  endTime: string;
  slotType: string;
}

interface EditSlotTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: SlotTemplate; // The template data to edit
  onUpdateSuccess: () => void; // Function to refresh the table
}

const EditSlotTemplateModal: React.FC<EditSlotTemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  onUpdateSuccess,
}) => {
  const [templateName, setTemplateName] = useState(template.name);
  const [slots, setSlots] = useState<FrontendSlot[]>([]);
  const [loading, setLoading] = useState(false);

  // ✅ Use useEffect to populate the form when the component receives the template
  useEffect(() => {
    setTemplateName(template.name);
    // Convert backend snake_case to frontend camelCase
    const formattedSlots =
      template.slots?.map((s) => ({
        startTime: s.start_time,
        endTime: s.end_time,
        slotType: s.slot_type,
      })) || [];
    setSlots(formattedSlots);
  }, [template]); // Rerun this effect if the template prop changes

  const addSlot = () => {
    setSlots([...slots, { startTime: "", endTime: "", slotType: "Work Slot" }]);
  };

  const removeSlot = (index: number) => {
    const updated = [...slots];
    updated.splice(index, 1);
    setSlots(updated);
  };

  const handleChange = (
    index: number,
    field: keyof FrontendSlot,
    value: string
  ) => {
    const updated = [...slots];
    updated[index][field] = value;
    setSlots(updated);
  };

  const handleSubmit = async () => {
    if (!templateName.trim()) {
      showToast("Please enter a template name", 'error');
      return;
    }
    const token = sessionStorage.getItem("accessToken");

    // Convert frontend camelCase back to backend snake_case
    const formattedSlots = slots.map((s) => ({
      start_time: s.startTime,
      end_time: s.endTime,
      slot_type: s.slotType,
    }));

    const payload = {
      name: templateName,
      slots: formattedSlots,
    };

    try {
      setLoading(true);
      // ✅ Use PUT request to update the existing template by its ID
      await api.appointment_api.put(
        `/api/slot-templates/${template._id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      showToast("Slot template updated successfully!", 'success');
      onUpdateSuccess(); // Trigger the data refresh in the parent
      onClose(); // Close this modal
    } catch (err: any) {
      console.error("❌ Error updating slot template:", err);
      showToast(err.response?.data?.message || "Something went wrong.", 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="slot-view-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="slot-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="slot-view-modal-header">
          <h2>Edit Slot Template</h2>
          <button className="slot-view-close-icon" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        <div className="slot-view-modal-body">
          <label className="slot-view-field-label">Slot Template Name *</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name"
            className="slot-view-input"
          />

          <h3>Slot Grid Table</h3>
          {/* The rest of the JSX is the same as your Add modal */}
          <table className="slot-view-slot-table">
            <thead>
              <tr>
                <th>START TIME</th>
                <th>END TIME</th>
                <th>SLOT TYPE</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((slot, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="time"
                      value={slot.startTime}
                      onChange={(e) =>
                        handleChange(index, "startTime", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={slot.endTime}
                      onChange={(e) =>
                        handleChange(index, "endTime", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <select
                      value={slot.slotType}
                      onChange={(e) =>
                        handleChange(index, "slotType", e.target.value)
                      }
                    >
                      <option value="Work Slot">Work Slot</option>
                      <option value="Break">Break</option>
                      {/* <option value="Lunch Break">Lunch Break</option> */}
                    </select>
                  </td>
                  <td>
                    <button
                      className="slot-view-delete-btn"
                      onClick={() => removeSlot(index)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="18" fill="#d32f2f" viewBox="0 0 24 24">
                        <path d="M3 6h18v2H3V6zm2 3h14l-1.5 12.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 9zm5 2v9h2v-9H10zm4 0v9h2v-9h-2zM9 4V2h6v2h5v2H4V4h5z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="slot-view-add-slot" onClick={addSlot}>
            + Add Slot
          </button>
          <div className="slot-view-modal-actions">
            <button className="slot-view-close-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              className="slot-view-create-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {/* ✅ Changed Button Text */}
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSlotTemplateModal;